import { memo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { mapStatus } from "../../utils/functions";
import { deliveryStatusEnum } from "../../helpers/enums.js";


const NormalDeliveryItemTable = ({
  deliveryItems,
  deliveryItemsCount,
  pagination,
  handleChangePage,
  handleChangeRowsPerPage,
  handleDeleteDeliveryItem,
  handleItemStatusUpdate,
  activeStatusToEdit,
  getButtonLabelText,
  getEmptyLabelText,
  editStatus,
  setEditStatus,
  selectedItemStatus,
  setSelectedItemStatus,
  isProcessingItem,
  handleOpenPdf,
  loading,
  isWaitingForAssignment,
  itemHasLogistics,
  openLogisticsDialog,
}) => {
  const colSpan = isWaitingForAssignment ? 7 : 6;

  return (
    <>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="tabla de bultos">
          <TableHead sx={{ backgroundColor: "#f9fafb" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold", pt: "5px", pb: "10px" }}>
                ID Item
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", pt: "5px", pb: "10px" }}>
                Índice
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", pt: "5px", pb: "10px" }}>
                Tracking
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", pt: "5px", pb: "10px" }}>
                Estado
              </TableCell>
              {isWaitingForAssignment && (
                <TableCell sx={{ fontWeight: "bold", pt: "5px", pb: "10px" }}>
                  Fulfillment
                </TableCell>
              )}
              <TableCell
                sx={{ fontWeight: "bold", pt: "5px", pb: "10px" }}
                align="center"
              >
                Etiqueta
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", pt: "5px", pb: "10px" }}
                align="center"
              >
                Acciones
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && deliveryItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : deliveryItems && deliveryItems.length > 0 ? (
              deliveryItems.map((item) => (
                <TableRow
                  key={item.id}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell>{item.id}</TableCell>
                  <TableCell>
                    Bulto {item.item_index} de {item.total_items || "?"}
                  </TableCell>
                  <TableCell>
                    {item.tracking_number || (
                      <Typography variant="caption" color="text.secondary">
                        Pendiente...
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {editStatus.state && item.id === editStatus.id ? (
                      <Autocomplete
                        fullWidth
                        id="shipping-item-select"
                        options={deliveryStatusEnum}
                        getOptionLabel={(deliveryStatus) =>
                          deliveryStatus ? deliveryStatus.label : ""
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.value === value?.value
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            sx={{ mt: "10px !important" }}
                            label="estado del bulto"
                            helperText={
                              selectedItemStatus === null && "Campo obligatorio"
                            }
                            error={selectedItemStatus === null}
                          />
                        )}
                        value={selectedItemStatus}
                        onChange={(event, status) =>
                          handleItemStatusUpdate(item, status.value)
                        }
                      />
                    ) : (
                      <>
                        {mapStatus(item.status)}
                        <IconButton
                          color="primary"
                          onClick={() => {
                            setEditStatus({ id: item.id, state: true });
                            setSelectedItemStatus(
                              deliveryStatusEnum.find(
                                (data) => data.value === item.status,
                              ),
                            );
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                  {isWaitingForAssignment && (
                    <TableCell>
                      {itemHasLogistics(item) ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Listo"
                          color="success"
                          size="small"
                          variant="outlined"
                          onClick={() => openLogisticsDialog(item)}
                          sx={{ cursor: "pointer" }}
                        />
                      ) : (
                        <Chip
                          icon={<HourglassEmptyIcon />}
                          label="Pendiente"
                          color="warning"
                          size="small"
                          variant="outlined"
                          onClick={() => openLogisticsDialog(item)}
                          sx={{ cursor: "pointer" }}
                        />
                      )}
                    </TableCell>
                  )}
                  <TableCell align="center">
                    {item.label ? (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={() => handleOpenPdf(item.label)}
                        sx={{ textTransform: "none" }}
                      >
                        {getButtonLabelText()}
                      </Button>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {getEmptyLabelText()}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteDeliveryItem(item.id)}
                      disabled={
                        isProcessingItem ||
                        deliveryItems.length <= 1 ||
                        activeStatusToEdit()
                      }
                      title={
                        deliveryItems.length <= 1
                          ? "No puedes eliminar el único bulto"
                          : "Eliminar bulto"
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No hay bultos registrados para esta orden.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 50, 100, 150, 200]}
        component="div"
        count={deliveryItemsCount}
        rowsPerPage={pagination.rowsPerPage}
        page={pagination.page - 1}
        onPageChange={(e, newPage) => handleChangePage(newPage + 1)}
        onRowsPerPageChange={(e) =>
          handleChangeRowsPerPage(parseInt(e.target.value, 10))
        }
      />
    </>
  );
};

export default memo(NormalDeliveryItemTable);
